import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { ApiError, ApiServiceError } from "libs/api";
import { MutationOptions } from "libs/reactQuery";

import { ManualReturnContext, TicketReturnContext } from "../../aiTickets.types";
import { SUPPORT_TICKETS_QUERY_KEY } from "../../constants/enums";
import {
  CreateManualReturnBody,
  requestCreateManualReturn,
  requestManualReturnContext,
  requestManualReturnOrderContext,
} from "./manualReturn.request";

export const useManualReturnContext = (whatsappConversationId: string | null) => {
  return useQuery<ManualReturnContext, ApiServiceError<ApiError>>({
    queryKey: [SUPPORT_TICKETS_QUERY_KEY, "manual-return", whatsappConversationId],
    queryFn: async () =>
      await requestManualReturnContext(whatsappConversationId as string),
    enabled: !!whatsappConversationId,
    onError: toastServerError,
  });
};

// The form's payload for one order on the manual path, where there is no ticket
// to key it on. Fetched only once an order is chosen — it carries presigned
// media urls that expire.
export const useManualReturnOrderContext = (
  whatsappConversationId: string | null,
  orderId: string | null,
) => {
  return useQuery<TicketReturnContext, ApiServiceError<ApiError>>({
    queryKey: [
      SUPPORT_TICKETS_QUERY_KEY,
      "manual-return-order",
      whatsappConversationId,
      orderId,
    ],
    queryFn: async () =>
      await requestManualReturnOrderContext(
        whatsappConversationId as string,
        orderId as string,
      ),
    enabled: !!whatsappConversationId && !!orderId,
    onError: toastServerError,
  });
};

export const useCreateManualReturn = (
  options?: MutationOptions<void, ApiServiceError<ApiError>, CreateManualReturnBody>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestCreateManualReturn,
    onError: toastServerError,
    ...options,
    onSuccess: (data, vars, ctx) => {
      // The order leaves Delivered, so the picker's list is stale — and any
      // ticket the assistant had open on it now shows a return exists.
      queryClient.invalidateQueries({
        queryKey: [SUPPORT_TICKETS_QUERY_KEY, "list"],
      });
      queryClient.invalidateQueries({
        queryKey: [
          SUPPORT_TICKETS_QUERY_KEY,
          "manual-return",
          vars.whatsappConversationId,
        ],
      });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};
